import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles, panelStyles, badgeStyles } from '../styles/shared.js';
import type { ModelerController } from '../controllers/modeler-controller.js';
import { VERIFICATION_METHODS, VERIFICATION_STATUSES, generationChanged } from '../utils/constants.js';

// ---------------------------------------------------------------------------
// <sysml-verification-panel> -- Requirements verification
//
// Ported from: SysmlVerificationPanel.vue
//
// Features:
// - Coverage metrics (pie-style summary)
// - Verification cases list with status badges
// - Requirements linkage (satisfy, verify, refine)
// - Add/edit verification case capability
// - Filter by status
// ---------------------------------------------------------------------------

interface VerificationRow {
  reqId: string;
  reqName: string;
  reqDoc?: string;
  verCases: {
    id: string;
    name: string;
    method: string;
    status: string;
  }[];
  overallStatus: 'verified' | 'partial' | 'unverified';
}

type StatusFilter = 'all' | 'verified' | 'partial' | 'unverified';

@customElement('sysml-verification-panel')
export class SysmlVerificationPanel extends LitElement {
  static override styles = [
    baseStyles,
    panelStyles,
    badgeStyles,
    css`
      :host { display: flex; flex-direction: column; height: 100%; }

      /* -- Coverage Summary -- */
      .coverage-bar {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        background: var(--r5-surface-muted);
      }
      .coverage-segments {
        flex: 1;
        height: 8px;
        border-radius: 4px;
        overflow: hidden;
        display: flex;
        background: var(--r5-border);
      }
      .coverage-seg {
        height: 100%;
        transition: width 0.4s var(--r5-ease-default);
      }
      .coverage-seg.verified { background: var(--r5-success-500); }
      .coverage-seg.partial { background: var(--r5-warning-500); }
      .coverage-seg.unverified { background: var(--r5-destructive-400); }
      .coverage-pct {
        font-size: var(--r5-text-xs);
        font-weight: 600;
        min-width: 36px;
        text-align: right;
        font-variant-numeric: tabular-nums;
      }

      /* -- Filter tabs -- */
      .filter-bar {
        display: flex;
        gap: var(--r5-space-1);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        flex-wrap: wrap;
      }
      .filter-btn {
        display: inline-flex;
        align-items: center;
        gap: var(--r5-space-1);
        padding: 2px var(--r5-space-2);
        font-size: var(--r5-text-xs);
        font-weight: 500;
        border: 1px solid var(--r5-border);
        border-radius: var(--r5-radius-full);
        background: var(--r5-surface);
        color: var(--r5-text-muted);
        cursor: pointer;
        transition: all var(--r5-duration-fast) var(--r5-ease-default);
      }
      .filter-btn:hover { border-color: var(--r5-primary); color: var(--r5-text); }
      .filter-btn.active {
        border-color: var(--r5-primary);
        background: color-mix(in srgb, var(--r5-primary) 10%, transparent);
        color: var(--r5-primary);
      }
      .filter-count {
        font-variant-numeric: tabular-nums;
      }

      /* -- Table -- */
      table {
        width: 100%;
        border-collapse: collapse;
        font-size: var(--r5-text-xs);
      }
      thead { background: var(--r5-surface-muted); position: sticky; top: 0; z-index: 1; }
      th {
        padding: var(--r5-space-2) var(--r5-space-3);
        text-align: left;
        font-weight: 600;
        font-size: var(--r5-text-xs);
        color: var(--r5-text-muted);
        text-transform: uppercase;
        letter-spacing: 0.05em;
        border-bottom: 1px solid var(--r5-border);
      }
      td {
        padding: var(--r5-space-2) var(--r5-space-3);
        border-bottom: 1px solid var(--r5-border);
        vertical-align: top;
      }
      tr.clickable { cursor: pointer; }
      tr.clickable:hover { background: color-mix(in srgb, var(--r5-primary) 6%, transparent); }

      /* -- Verification cases within a row -- */
      .ver-cases {
        display: flex;
        flex-direction: column;
        gap: var(--r5-space-1);
      }
      .ver-case {
        display: flex;
        align-items: center;
        gap: var(--r5-space-1);
        font-size: var(--r5-text-xs);
      }
      .method-pill {
        display: inline-flex;
        padding: 1px var(--r5-space-1);
        border-radius: var(--r5-radius-sm);
        font-size: var(--r5-font-size-badge);
        font-weight: 500;
        background: var(--r5-surface-muted);
        color: var(--r5-text-muted);
        text-transform: uppercase;
        letter-spacing: 0.03em;
      }
      .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        flex-shrink: 0;
      }
      .status-dot.planned { background: var(--r5-text-muted); }
      .status-dot.passed { background: var(--r5-success-500); }
      .status-dot.failed { background: var(--r5-destructive-500); }
      .status-dot.blocked { background: var(--r5-warning-500); }

      /* -- Req doc preview -- */
      .req-doc {
        font-size: var(--r5-font-size-badge);
        color: var(--r5-text-muted);
        margin-top: 2px;
        line-height: 1.3;
        max-height: 2.6em;
        overflow: hidden;
      }

      /* -- Add form -- */
      .add-bar {
        display: flex;
        align-items: center;
        gap: var(--r5-space-2);
        padding: var(--r5-space-2) var(--r5-space-3);
        border-top: 1px solid var(--r5-border);
        flex-shrink: 0;
      }
    `,
  ];

  // CRITICAL: hasChanged: generationChanged for controller properties
  @property({ attribute: false, hasChanged: generationChanged }) modeler!: ModelerController;

  @state() private _filter: StatusFilter = 'all';
  @state() private _showAddForm = false;
  @state() private _newVcName = '';
  @state() private _newVcSubject = '';
  @state() private _newVcMethod = 'test';

  override render() {
    const rows = this._buildRows();
    const filtered = this._filter === 'all' ? rows : rows.filter((r) => r.overallStatus === this._filter);
    const verified = rows.filter((r) => r.overallStatus === 'verified').length;
    const partial = rows.filter((r) => r.overallStatus === 'partial').length;
    const unverified = rows.filter((r) => r.overallStatus === 'unverified').length;
    const coveragePct = rows.length > 0 ? Math.round(((verified + partial * 0.5) / rows.length) * 100) : 0;

    return html`
      <div class="panel-header">
        <h3>Verification</h3>
        <div style="display: flex; gap: var(--r5-space-1); align-items: center;">
          <span class="badge badge-neutral">${rows.length} req${rows.length !== 1 ? 's' : ''}</span>
          <ark-button size="xs" variant="ghost" @click=${() => (this._showAddForm = !this._showAddForm)}>
            <ark-icon name="plus" size="xs"></ark-icon>
          </ark-button>
        </div>
      </div>

      ${this._renderCoverageBar(rows.length, verified, partial, unverified, coveragePct)}
      ${this._renderFilterBar(rows.length, verified, partial, unverified)}

      <div class="panel-body" style="padding: 0;">
        ${filtered.length === 0
          ? html`
              <div class="empty-state">
                <ark-icon name="list-checks" size="lg"></ark-icon>
                <div>
                  ${rows.length === 0
                    ? 'No requirements found in the model.'
                    : `No ${this._filter} requirements.`}
                </div>
              </div>
            `
          : html`
              <table>
                <thead>
                  <tr>
                    <th>Requirement</th>
                    <th>Status</th>
                    <th>Verification Cases</th>
                  </tr>
                </thead>
                <tbody>
                  ${filtered.map((row) => this._renderRow(row))}
                </tbody>
              </table>
            `}
      </div>

      ${this._showAddForm ? this._renderAddForm() : nothing}
    `;
  }

  private _renderCoverageBar(total: number, verified: number, partial: number, unverified: number, pct: number) {
    if (total === 0) return nothing;
    return html`
      <div class="coverage-bar">
        <span style="font-size: var(--r5-text-xs); color: var(--r5-text-muted);">Coverage</span>
        <div class="coverage-segments">
          <div class="coverage-seg verified" style="width: ${total > 0 ? (verified / total) * 100 : 0}%"></div>
          <div class="coverage-seg partial" style="width: ${total > 0 ? (partial / total) * 100 : 0}%"></div>
          <div class="coverage-seg unverified" style="width: ${total > 0 ? (unverified / total) * 100 : 0}%"></div>
        </div>
        <span class="coverage-pct">${pct}%</span>
      </div>
    `;
  }

  private _renderFilterBar(total: number, verified: number, partial: number, unverified: number) {
    const filters: { key: StatusFilter; label: string; count: number }[] = [
      { key: 'all', label: 'All', count: total },
      { key: 'verified', label: 'Verified', count: verified },
      { key: 'partial', label: 'Partial', count: partial },
      { key: 'unverified', label: 'Unverified', count: unverified },
    ];

    return html`
      <div class="filter-bar">
        ${filters.map(
          (f) => html`
            <button
              class="filter-btn ${this._filter === f.key ? 'active' : ''}"
              @click=${() => (this._filter = f.key)}
            >
              ${f.label} <span class="filter-count">(${f.count})</span>
            </button>
          `,
        )}
      </div>
    `;
  }

  private _renderRow(row: VerificationRow) {
    const statusBadge =
      row.overallStatus === 'verified'
        ? 'badge-success'
        : row.overallStatus === 'partial'
          ? 'badge-warning'
          : 'badge-error';

    return html`
      <tr class="clickable" @click=${() => this._onReqClick(row.reqId)}>
        <td>
          <div>${row.reqName}</div>
          ${row.reqDoc ? html`<div class="req-doc">${row.reqDoc}</div>` : nothing}
        </td>
        <td><span class="badge ${statusBadge}">${row.overallStatus}</span></td>
        <td>
          ${row.verCases.length === 0
            ? html`<span style="color: var(--r5-text-muted); font-style: italic; font-size: var(--r5-text-xs);">No verification cases</span>`
            : html`
                <div class="ver-cases">
                  ${row.verCases.map(
                    (vc) => html`
                      <div class="ver-case">
                        <span class="status-dot ${vc.status}"></span>
                        <span class="method-pill">${vc.method}</span>
                        <span>${vc.name}</span>
                      </div>
                    `,
                  )}
                </div>
              `}
        </td>
      </tr>
    `;
  }

  private _renderAddForm() {
    return html`
      <div class="add-bar" style="flex-direction: column; align-items: stretch; gap: var(--r5-space-2);">
        <div style="font-size: var(--r5-text-xs); font-weight: 600;">Add Verification Case</div>
        <input
          type="text"
          placeholder="Verification case name"
          .value=${this._newVcName}
          @input=${(e: InputEvent) => (this._newVcName = (e.target as HTMLInputElement).value)}
          style="padding: var(--r5-space-1) var(--r5-space-2); border: 1px solid var(--r5-border); border-radius: var(--r5-radius-sm); font-size: var(--r5-text-xs);"
        />
        <div style="display: flex; gap: var(--r5-space-2);">
          <select
            .value=${this._newVcSubject}
            @change=${(e: Event) => (this._newVcSubject = (e.target as HTMLSelectElement).value)}
            style="flex: 1; padding: var(--r5-space-1) var(--r5-space-2); border: 1px solid var(--r5-border); border-radius: var(--r5-radius-sm); font-size: var(--r5-text-xs);"
          >
            <option value="">Subject requirement...</option>
            ${this.modeler.requirements.map(
              (req) => html`<option value=${req.name}>${req.name}</option>`,
            )}
          </select>
          <select
            .value=${this._newVcMethod}
            @change=${(e: Event) => (this._newVcMethod = (e.target as HTMLSelectElement).value)}
            style="width: 100px; padding: var(--r5-space-1) var(--r5-space-2); border: 1px solid var(--r5-border); border-radius: var(--r5-radius-sm); font-size: var(--r5-text-xs);"
          >
            ${VERIFICATION_METHODS.map(
              (m) => html`<option value=${m.value}>${m.label}</option>`,
            )}
          </select>
        </div>
        <div style="display: flex; gap: var(--r5-space-2); justify-content: flex-end;">
          <ark-button size="xs" variant="ghost" @click=${() => (this._showAddForm = false)}>Cancel</ark-button>
          <ark-button
            size="xs"
            variant="primary"
            ?disabled=${!this._newVcName.trim() || !this._newVcSubject}
            @click=${this._onAddVc}
          >
            Insert
          </ark-button>
        </div>
      </div>
    `;
  }

  private _buildRows(): VerificationRow[] {
    const m = this.modeler;
    const reqs = m.requirements;
    const verCases = m.verificationCases;

    return reqs.map((req) => {
      const linked = verCases.filter(
        (vc) =>
          vc.subject?.ref === req.name ||
          vc.subject?.ref === req.id,
      );

      // Check statuses of linked verification cases
      const hasAnyCase = linked.length > 0;
      const allPassed = hasAnyCase && linked.every(
        (vc) => (vc.meta?.state ?? 'draft') === 'done',
      );

      const overallStatus: VerificationRow['overallStatus'] = !hasAnyCase
        ? 'unverified'
        : allPassed
          ? 'verified'
          : 'partial';

      return {
        reqId: req.id,
        reqName: req.name,
        reqDoc: req.text,
        verCases: linked.map((vc) => {
          // Normalize parser state values to CSS dot classes
          const rawState = (vc.meta?.state as string) ?? '';
          const statusMap: Record<string, string> = {
            done: 'passed', passed: 'passed', complete: 'passed',
            failed: 'failed', fail: 'failed',
            blocked: 'blocked',
            wip: 'planned', draft: 'planned', active: 'planned',
          };
          return {
            id: vc.id,
            name: vc.name,
            method: (vc.meta?.kind as string) ?? 'test',
            status: statusMap[rawState] ?? 'planned',
          };
        }),
        overallStatus,
      };
    });
  }

  private _onReqClick(reqId: string): void {
    const req = this.modeler.requirements.find((r) => r.id === reqId);
    if (req?.source) {
      this.modeler.revealInEditor(req.source);
    }
  }

  private _onAddVc(): void {
    if (!this._newVcName.trim() || !this._newVcSubject) return;

    const code = `\nverification def ${this._newVcName.trim()} {\n  doc /* Verify via ${this._newVcMethod}. */\n  subject : ${this._newVcSubject};\n}`;

    this.dispatchEvent(
      new CustomEvent('insert-code', {
        detail: { code },
        bubbles: true,
        composed: true,
      }),
    );

    this._newVcName = '';
    this._newVcSubject = '';
    this._newVcMethod = 'test';
    this._showAddForm = false;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-verification-panel': SysmlVerificationPanel;
  }
}
